import './MapPopup.scss';
import maplibregl, { type PopupOptions } from 'maplibre-gl';
import { useEffect, useEffectEvent, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/core/utils';
import { useMap, useOverlays } from './mapContext';

interface Props {
  lng: number;
  lat: number;
  open: boolean;
  /** Read once. `className` adds to the shared `map-popup` look; `anchor` defaults to `bottom`. */
  options?: PopupOptions;
  /** The user closed the popup (close button / map click). */
  onClose?: () => void;
  children: ReactNode;
}

/** A MapLibre popup whose content is rendered by React, in the app's popup style (MapPopup.scss). */
export function MapPopup({ lng, lat, open, options, onClose, children }: Props) {
  const map = useMap();
  const overlays = useOverlays();
  const handleClose = useEffectEvent(() => onClose?.());
  const [container] = useState(() => document.createElement('div'));
  // Initializers must stay side-effect free (StrictMode runs them twice), so content is attached in an effect.
  // A fixed anchor: MapLibre's automatic one flips the popup around its point as it nears the map edge, which
  // jumps under the user's finger while panning. Near an edge the popup clips instead; the user pans to see it.
  // Subpixel: whole-pixel rounding makes the popup wobble against the smoothly moving map (and its marker).
  const [popup] = useState(
    () =>
      new maplibregl.Popup({
        anchor: 'bottom',
        subpixelPositioning: true,
        ...options,
        className: cx('map-popup', options?.className),
      }),
  );

  useEffect(() => void popup.setDOMContent(container), [popup, container]);

  useEffect(() => {
    if (!map || !open) return;
    popup.setLngLat([lng, lat]).addTo(map);
    // Listen only while we hold it open, so our own remove() below does not report a user close.
    const listener = () => handleClose();
    popup.on('close', listener);
    return () => {
      popup.off('close', listener);
      popup.remove();
    };
  }, [map, popup, open, lng, lat]);

  useEffect(
    () =>
      overlays.register(() => {
        if (popup.isOpen()) popup.setLngLat(popup.getLngLat());
      }),
    [overlays, popup],
  );

  return createPortal(children, container);
}
